import { useState, useEffect, FormEvent } from 'react'
import { superadminApi } from '@/services/api/superadmin'
import { subjectFacultyApi } from '@/services/api/director'
import type { SubjectFacultyLink } from '@/services/api/director'

interface TeacherParam {
  id: string
  name: string
  code: string | null
  description: string | null
  is_active: boolean
  color?: string | null
}

export function TeacherParamsPage() {
  const [activeTab, setActiveTab] = useState<'backgrounds' | 'subjects' | 'faculties'>('backgrounds')
  const [items, setItems] = useState<TeacherParam[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', code: '', description: '', color: '' })
  const [submitting, setSubmitting] = useState(false)
  // V5/C.3: Fach-Fakultät-Zuordnungen (nur Subjects-Tab relevant)
  const [facultyLinks, setFacultyLinks] = useState<SubjectFacultyLink[]>([])
  const [faculties, setFaculties] = useState<TeacherParam[]>([])
  const [assignSubject, setAssignSubject] = useState<string | null>(null)
  const [assignFaculty, setAssignFaculty] = useState('')

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await (activeTab === 'backgrounds' ? superadminApi.listBackgrounds()
        : activeTab === 'subjects' ? superadminApi.listSubjects()
        : superadminApi.listFaculties())
      setItems(res)
      if (activeTab === 'subjects') {
        const [links, facs] = await Promise.all([
          subjectFacultyApi.list(),
          superadminApi.listFaculties(),
        ])
        setFacultyLinks(links)
        setFaculties(facs)
      }
    } catch {
      setError(`${labels[activeTab]} konnten nicht geladen werden`)
    } finally {
      setLoading(false)
    }
  }

  const assignFacultyToSubject = async () => {
    if (!assignSubject || !assignFaculty) return
    try {
      await subjectFacultyApi.create({ subject_id: assignSubject, faculty_id: assignFaculty })
      setMessage('Fakultät zugewiesen')
      setError(null)
      setAssignSubject(null)
      setAssignFaculty('')
      await fetchItems()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Zuweisung fehlgeschlagen')
    }
  }

  const removeFacultyFromSubject = async (linkId: string) => {
    try {
      await subjectFacultyApi.remove(linkId)
      await fetchItems()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Entfernen fehlgeschlagen')
    }
  }

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const payload: { name: string; code?: string; description?: string; color?: string } = {
        name: form.name,
        code: form.code || undefined,
        description: form.description || undefined,
      }
      if (activeTab === 'faculties') payload.color = form.color || undefined

      await (activeTab === 'backgrounds' ? superadminApi.createBackground(payload)
        : activeTab === 'subjects' ? superadminApi.createSubject(payload)
        : superadminApi.createFaculty(payload))

      setShowCreate(false)
      setForm({ name: '', code: '', description: '', color: '' })
      setMessage(`${labels[activeTab]} erstellt`)
      setError(null)
      await fetchItems()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Fehler beim Erstellen')
    } finally {
      setSubmitting(false)
    }
  }

  useEffect(() => { fetchItems() }, [activeTab])

  if (loading) return <div className="p-6">Laden...</div>

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Lehrer-Parameter</h1>
        <button onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + {labels[activeTab]} erstellen
        </button>
      </div>

      {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="flex space-x-1 mb-6 border-b">
        <button onClick={() => setActiveTab('backgrounds')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'backgrounds' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Backgrounds</button>
        <button onClick={() => setActiveTab('subjects')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'subjects' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Subjects</button>
        <button onClick={() => setActiveTab('faculties')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'faculties' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}>Faculties</button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beschreibung</th>
              {activeTab === 'subjects' && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fakultät(en)</th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 flex items-center gap-2">
                  {item.color && <span className="w-4 h-4 rounded" style={{ backgroundColor: item.color ?? undefined }} />}
                  {item.name}
                </td>
                <td className="px-6 py-4">{item.code || '-'}</td>
                <td className="px-6 py-4">{item.description || '-'}</td>
                {activeTab === 'subjects' && (
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap items-center gap-1">
                      {facultyLinks
                        .filter(l => l.subject_id === item.id)
                        .map(l => (
                          <span key={l.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700">
                            {l.faculty_name}
                            <button
                              type="button"
                              onClick={() => removeFacultyFromSubject(l.id)}
                              className="text-indigo-400 hover:text-indigo-600"
                              title="Zuordnung entfernen"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      <button
                        type="button"
                        onClick={() => { setAssignSubject(item.id); setAssignFaculty('') }}
                        className="text-xs text-blue-600 hover:text-blue-800 px-1"
                        title="Fakultät zuweisen"
                      >
                        +
                      </button>
                    </div>
                  </td>
                )}
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {assignSubject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-bold mb-4">Fakultät zuweisen</h2>
            <select
              value={assignFaculty}
              onChange={e => setAssignFaculty(e.target.value)}
              className="w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 text-sm"
            >
              <option value="">-- Fakultät wählen --</option>
              {faculties.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Basis für das Routing Lektion → Fakultät.
            </p>
            <div className="mt-6 flex justify-end space-x-3">
              <button type="button" onClick={() => setAssignSubject(null)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button type="button" disabled={!assignFaculty} onClick={assignFacultyToSubject} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">Zuweisen</button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{labels[activeTab]} erstellen</h2>
            <form onSubmit={handleCreate}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Code</label>
                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
              </div>
              {activeTab === 'faculties' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Farbe</label>
                  <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} type="color" className="mt-1 block w-full h-10 rounded-md border-gray-300 shadow-sm" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Beschreibung</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" rows={3} />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">{submitting ? 'Erstellen...' : 'Erstellen'}</button>
            </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

const labels = {
  backgrounds: 'Background',
  subjects: 'Subject',
  faculties: 'Faculty',
}
